<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-tags me-2" style="color: #ef4444;"></i>{{ $t('Discount Report') }}
          </h5>
          <small class="text-muted">{{ $t('Finance & Payments') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Discounts') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ formatCurrency(summary.total_discount) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Discount Rate') }}</div>
            <div class="fw-bold fs-4 text-warning">{{ (summary.discount_rate || 0).toFixed(1) }}%</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Orders with Discount') }}</div>
            <div class="fw-bold fs-4">{{ summary.orders_with_discount || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Avg. Discount') }}</div>
            <div class="fw-bold fs-4">{{ formatCurrency(summary.avg_discount) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Discount Types Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Discount Type') }}</th>
                <th class="text-end">{{ $t('Times Used') }}</th>
                <th class="text-end">{{ $t('Total Amount') }}</th>
                <th class="text-end pe-3">%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!discountData.length && !loading">
                <td colspan="4" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="d in discountData" :key="d.type">
                <td class="ps-3 fw-medium">{{ $t(d.type) }}</td>
                <td class="text-end">{{ d.count }}</td>
                <td class="text-end fw-semibold text-danger">-{{ formatCurrency(d.amount) }}</td>
                <td class="text-end pe-3">{{ d.percent }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportDiscounts',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Discount Report',
      summary: { total_discount: 0, discount_rate: 0, orders_with_discount: 0, avg_discount: 0 },
      types: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.discountReport(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary  = payload.summary || { total_discount: 0, discount_rate: 0, orders_with_discount: 0, avg_discount: 0 };
      this.types    = payload.types   || [];
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Discount Report'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Total Discounts') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.total_discount), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Discount Rate') + '\n', style: 'kpiLabel' }, { text: `${(this.summary.discount_rate || 0).toFixed(1)}%`, style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Orders with Discount') + '\n', style: 'kpiLabel' }, { text: String(this.summary.orders_with_discount || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Avg. Discount') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.avg_discount), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Discount Type'), style: 'tableHeader' },
                { text: this.$t('Times Used'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Total Amount'), style: 'tableHeader', alignment: 'right' },
                { text: '%', style: 'tableHeader', alignment: 'right' },
              ],
              ...this.discountData.map(d => [
                { text: this.$t(d.type), style: 'tableCell' },
                { text: String(d.count), style: 'tableCell', alignment: 'right' },
                { text: `-${this.formatCurrencyRaw(d.amount)}`, style: 'tableCell', alignment: 'right', color: '#ef4444' },
                { text: `${d.percent}%`, style: 'tableCell', alignment: 'right' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ];
      return { content };
    },
  },
  computed: {
    discountData() {
      return this.types || [];
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
